import { useCallback, useEffect, useState } from 'react';
import { LaptopMinimalIcon, Loader2Icon, RefreshCcwIcon, ShieldOffIcon, SmartphoneIcon } from 'lucide-react';
import { listActiveSessions, revokeOtherSessions, revokeSessionByToken, type ActiveSessionRecord } from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

type AccountSessionsPanelProps = {
  currentSessionId: string;
  refreshSignal?: number;
};

function formatDate(value: string) {
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function normalizeSessions(sessions: ActiveSessionRecord[], currentSessionId: string) {
  return [...sessions].sort((left, right) => {
    if (left.id === currentSessionId && right.id !== currentSessionId) {
      return -1;
    }

    if (right.id === currentSessionId && left.id !== currentSessionId) {
      return 1;
    }

    return new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
  });
}

function inferDeviceLabel(userAgent?: string | null) {
  if (!userAgent) {
    return 'Navigateur non identifié';
  }

  const normalized = userAgent.toLowerCase();

  if (/iphone|ipad|android|mobile/.test(normalized)) {
    return 'Appareil mobile';
  }

  if (/macintosh|windows|linux|x11/.test(normalized)) {
    return 'Poste desktop';
  }

  return 'Navigateur web';
}

function inferBrowserLabel(userAgent?: string | null) {
  if (!userAgent) {
    return 'User-Agent indisponible';
  }

  const normalized = userAgent.toLowerCase();

  if (normalized.includes('edg/')) {
    return 'Microsoft Edge';
  }

  if (normalized.includes('chrome/') && !normalized.includes('edg/')) {
    return 'Google Chrome';
  }

  if (normalized.includes('firefox/')) {
    return 'Mozilla Firefox';
  }

  if (normalized.includes('safari/') && !normalized.includes('chrome/')) {
    return 'Safari';
  }

  return userAgent;
}

export function AccountSessionsPanel({
  className,
  currentSessionId,
  refreshSignal = 0,
}: AccountSessionsPanelProps & React.ComponentProps<'section'>) {
  const [sessions, setSessions] = useState<ActiveSessionRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [revokingToken, setRevokingToken] = useState<string | null>(null);
  const [isRevokingOthers, setIsRevokingOthers] = useState(false);

  const fetchSessions = useCallback(async (manualRefresh = false) => {
    if (manualRefresh) {
      setIsRefreshing(true);
    }

    const result = await listActiveSessions();

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      setSessions([]);
      setIsLoading(false);
      setIsRefreshing(false);
      return;
    }

    setSessions(normalizeSessions(result.data ?? [], currentSessionId));
    setIsLoading(false);
    setIsRefreshing(false);
  }, [currentSessionId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetching sessions is an external sync triggered by the refresh signal.
    setIsLoading(true);
    void fetchSessions();
  }, [fetchSessions, refreshSignal]);

  const otherSessions = sessions.filter((session) => session.id !== currentSessionId);

  async function handleRevokeSession(session: ActiveSessionRecord) {
    setRevokingToken(session.token);

    const result = await revokeSessionByToken(session.token);

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      setRevokingToken(null);
      return;
    }

    toast.success('Session révoquée.');
    setRevokingToken(null);
    await fetchSessions(true);
  }

  async function handleRevokeOtherSessions() {
    setIsRevokingOthers(true);

    const result = await revokeOtherSessions();

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      setIsRevokingOthers(false);
      return;
    }

    toast.success('Les autres sessions ont été révoquées.');
    setIsRevokingOthers(false);
    await fetchSessions(true);
  }

  return (
    <section
      data-testid="account-sessions-panel"
      className={cn('rounded-[var(--radius-surface-4xl)] border border-border bg-card p-6 shadow-[0_8px_24px_rgba(15,23,42,0.03)]', className)}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold text-foreground">Sessions actives</h2>
          <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
            Visualise les sessions encore valides sur ce compte et révoque celles qui ne devraient plus avoir accès.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            data-testid="account-sessions-refresh"
            type="button"
            variant="outline"
            onClick={() => void fetchSessions(true)}
            disabled={isLoading || isRefreshing}
          >
            {isRefreshing ? <Loader2Icon className="animate-spin" /> : <RefreshCcwIcon />}
            Actualiser
          </Button>
          <Button
            data-testid="account-sessions-revoke-others"
            type="button"
            variant="outline"
            onClick={() => void handleRevokeOtherSessions()}
            disabled={isLoading || isRevokingOthers || otherSessions.length === 0}
          >
            {isRevokingOthers ? <Loader2Icon className="animate-spin" /> : <ShieldOffIcon />}
            Révoquer les autres sessions
          </Button>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <div key={`account-session-skeleton-${index}`} className="rounded-[var(--radius-surface-3xl)] border border-border p-5">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="mt-3 h-4 w-full" />
              <Skeleton className="mt-2 h-4 w-3/4" />
            </div>
          ))
        ) : sessions.length === 0 ? (
          <div className="rounded-[var(--radius-surface-3xl)] border border-dashed border-border bg-muted/60 p-6 text-sm text-muted-foreground">
            Aucune session active n a été trouvée.
          </div>
        ) : (
          sessions.map((session) => {
            const isCurrentSession = session.id === currentSessionId;
            const deviceLabel = inferDeviceLabel(session.userAgent);
            const browserLabel = inferBrowserLabel(session.userAgent);

            return (
              <article
                key={session.id}
                data-testid={isCurrentSession ? 'account-current-session-card' : 'account-other-session-card'}
                className={cn(
                  'rounded-[var(--radius-surface-3xl)] border p-5 transition-colors',
                  isCurrentSession ? 'border-sky-200 bg-sky-50/60 dark:border-sky-900/70 dark:bg-sky-950/20' : 'border-border bg-background/70',
                )}
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="inline-flex items-center gap-2 rounded-full bg-background px-3 py-1 text-sm font-medium text-foreground ring-1 ring-border">
                        {/mobile/i.test(deviceLabel) ? <SmartphoneIcon className="size-4" /> : <LaptopMinimalIcon className="size-4" />}
                        {deviceLabel}
                      </span>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-3 py-1 text-sm font-medium',
                          isCurrentSession ? 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-200' : 'bg-muted text-foreground',
                        )}
                      >
                        {isCurrentSession ? 'Session courante' : 'Autre session'}
                      </span>
                    </div>

                    <div className="space-y-1">
                      <p className="font-medium text-foreground">{browserLabel}</p>
                      <p className="text-sm text-muted-foreground">
                        Dernière activité le {formatDate(session.updatedAt)} • expire le {formatDate(session.expiresAt)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Créée le {formatDate(session.createdAt)} • IP {session.ipAddress || 'indisponible'}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col items-start gap-2 lg:items-end">
                    <p className="text-xs text-muted-foreground">ID {session.id.slice(0, 8)}...</p>
                    {!isCurrentSession ? (
                      <Button
                        data-testid="account-session-revoke-button"
                        type="button"
                        variant="outline"
                        onClick={() => void handleRevokeSession(session)}
                        disabled={revokingToken === session.token || isRevokingOthers}
                      >
                        {revokingToken === session.token ? <Loader2Icon className="animate-spin" /> : <ShieldOffIcon />}
                        Révoquer
                      </Button>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })
        )}
      </div>
    </section>
  );
}
